import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Send, Sparkles, Printer, CheckCircle, Eye, X, Upload, AlertTriangle, Clock, FileText, Search, Download, Trash2, CheckCircle2, ChevronDown, ChevronUp, ArrowLeftRight,
} from "lucide-react";
import {
  apiGet, apiPost, apiPatch, apiDelete, handleApiError, KpiCard, Btn, StatoBadge,
  STATO_RICETTA_LABELS, DESTINAZIONE_OPTIONS, formatDate, RICETTE_BASE, authHeaders, apiDownload,
  FilterBar, FilterSelect, DetailPanel, OspiteAvatar, ScadenzaCell, EmailTrackingBadges,
} from "./shared";
import axios from "axios";
import DoctorSelect from "../../../components/doctors/DoctorSelect";
import { useAuth } from "../../../context/AuthContext";

export default function RicetteMedico({ token }) {
  const { activeStruttura } = useAuth();
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [ospiti, setOspiti] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ fascicolo_id: "", doctor_id: "", medico: "", note: "" });
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterPeriodo, setFilterPeriodo] = useState("30");
  const [ospiteSearch, setOspiteSearch] = useState("");
  const [selectedOspite, setSelectedOspite] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [showAnomalie, setShowAnomalie] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listRes, kpiRes, ospitiRes] = await Promise.all([
        apiGet("/ricette-medico", token),
        apiGet("/ricette-medico/kpi", token),
        apiGet("/ospiti", token),
      ]);
      setItems(listRes.items || []);
      setKpi(kpiRes);
      setOspiti(ospitiRes.items || []);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCheckedIds(new Set());
  }, [search, filterStato, filterPeriodo, selectedOspite?.fascicolo_id]);

  const openDetail = async (id) => {
    try {
      const detail = await apiGet(`/ricette-medico/${id}`, token);
      setSelected(detail);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleCreate = async () => {
    if (!form.fascicolo_id) {
      toast.error("Seleziona un ospite");
      return;
    }
    setActionLoading(true);
    try {
      await apiPost("/ricette-medico", token, {
        fascicolo_id: form.fascicolo_id,
        doctor_id: form.doctor_id || undefined,
        medico: form.medico || undefined,
        note: form.note,
      });
      toast.success("Ricetta registrata");
      setShowCreate(false);
      setForm({ fascicolo_id: "", doctor_id: "", medico: "", note: "" });
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDestinazione = async (farmacoId, value) => {
    if (!selected) return;
    try {
      await apiPatch(`/ricette-medico/${selected.id}/farmaco-destinazione`, token, {
        farmaco_id: farmacoId,
        destinazione_ricetta: value,
      });
      openDetail(selected.id);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleAnalizzaIA = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await apiPost(`/ricette-medico/${selected.id}/analizza-ia`, token, {});
      toast.success("Analisi completata");
      openDetail(selected.id);
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleInviaFarmacia = async (targetIds) => {
    const ids = (targetIds || (checkedIds.size > 0 ? [...checkedIds] : selected ? [selected.id] : [])).filter(Boolean);
    if (ids.length === 0) {
      toast.info("Seleziona almeno una ricetta");
      return;
    }
    setActionLoading(true);
    try {
      if (ids.length === 1) {
        await apiPost(`/ricette-medico/${ids[0]}/invia-farmacia`, token, {});
        toast.success("Inviato alla farmacia");
      } else {
        const res = await apiPost("/ricette-medico/invia-farmacia-bulk", token, { ricetta_ids: ids });
        if (res.errori?.length) {
          toast.warning(`Inviate ${res.inviati}/${res.totale} ricette alla farmacia`);
        } else {
          toast.success(`${res.inviati} ricette inviate alla farmacia in un'unica email`);
        }
      }
      setCheckedIds(new Set());
      if (selected && ids.includes(selected.id)) {
        openDetail(selected.id);
      }
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStampa = async () => {
    if (!selected) return;
    try {
      await apiDownload(`/ricette-medico/${selected.id}/stampa-struttura`, token, `ricette_struttura_${selected.id.slice(0, 8)}.pdf`);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleCompleta = async (targetIds) => {
    const ids = (targetIds || (checkedIds.size > 0 ? [...checkedIds] : selected ? [selected.id] : [])).filter(Boolean);
    if (ids.length === 0) {
      toast.info("Seleziona almeno una ricetta");
      return;
    }
    const label = ids.length === 1 ? "questa ricetta" : `${ids.length} ricette`;
    if (!window.confirm(`Segnare ${label} come completata?`)) return;

    setActionLoading(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => apiPost(`/ricette-medico/${id}/completa`, token, {})),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;

      if (ok === 0) {
        handleApiError(results.find((r) => r.status === "rejected")?.reason, "Errore completamento");
        return;
      }
      if (fail > 0) {
        toast.warning(`${ok} completate, ${fail} non riuscite`);
      } else {
        toast.success(ok === 1 ? "Pratica completata" : `${ok} ricette segnate come completate`);
      }

      setCheckedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      if (selected && ids.includes(selected.id)) {
        setSelected(null);
      }
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSpostaARichiesta = async () => {
    if (!selected) return;
    if (!window.confirm("Spostare questa ricetta tra le Richieste farmacia? Gli allegati vengono preservati.")) return;
    setActionLoading(true);
    try {
      await apiPost(`/ricette-medico/${selected.id}/sposta-a-richiesta`, token, {});
      toast.success("Spostata tra le richieste farmacia");
      setSelected(null);
      load();
    } catch (err) {
      handleApiError(err, "Errore durante lo spostamento");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteRicette = async (ids) => {
    const uniqueIds = [...new Set(ids.filter(Boolean))];
    if (uniqueIds.length === 0) return;

    const sentCount = uniqueIds.filter((id) => {
      const item = items.find((r) => r.id === id) || (selected?.id === id ? selected : null);
      return item && ["inviata_farmacia", "completata"].includes(item.stato);
    }).length;

    const label = uniqueIds.length === 1 ? "questa ricetta" : `${uniqueIds.length} ricette`;
    let confirmMsg = `Eliminare ${label}? L'operazione non è reversibile.`;
    if (sentCount > 0) {
      confirmMsg += sentCount === 1
        ? " Una ricetta risulta già inviata alla farmacia."
        : ` ${sentCount} ricette risultano già inviate alla farmacia.`;
    }
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(true);
    try {
      const results = await Promise.allSettled(uniqueIds.map((id) => apiDelete(`/ricette-medico/${id}`, token)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;

      if (ok === 0) {
        handleApiError(results.find((r) => r.status === "rejected")?.reason, "Errore eliminazione");
        return;
      }
      if (fail > 0) {
        toast.warning(`${ok} eliminate, ${fail} non riuscite`);
      } else {
        toast.success(ok === 1 ? "Ricetta eliminata" : `${ok} ricette eliminate`);
      }

      setCheckedIds((prev) => {
        const next = new Set(prev);
        uniqueIds.forEach((id) => next.delete(id));
        return next;
      });
      if (selected && uniqueIds.includes(selected.id)) {
        setSelected(null);
      }
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    deleteRicette([selected.id]);
  };

  const handleDeleteSelected = () => {
    deleteRicette([...checkedIds]);
  };

  const handleDownload = async (fileId, fileName) => {
    if (!selected) return;
    try {
      await apiDownload(`/ricette-medico/${selected.id}/allegato/${fileId}/download`, token, fileName);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleView = async (fileId) => {
    if (!selected) return;
    try {
      const res = await axios.get(
        `${RICETTE_BASE}/ricette-medico/${selected.id}/allegato/${fileId}/download`,
        { headers: authHeaders(token), responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpload = async (e) => {
    if (!selected || !e.target.files?.[0]) return;
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    setActionLoading(true);
    try {
      await axios.post(`${RICETTE_BASE}/ricette-medico/${selected.id}/upload`, fd, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      toast.success("Allegato caricato");
      openDetail(selected.id);
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleOspiteChange = (fascicoloId) => {
    const ospite = ospiti.find((o) => o.fascicolo_id === fascicoloId);
    setForm({
      fascicolo_id: fascicoloId,
      doctor_id: ospite?.doctor_id || "",
      medico: ospite?.medico || "",
      note: form.note,
    });
  };

  const ospitiConRicette = useMemo(() => {
    const map = new Map();
    items.forEach((r) => {
      const key = r.fascicolo_id || `${r.ospite_cognome}-${r.ospite_nome}`;
      if (!map.has(key)) {
        map.set(key, {
          fascicolo_id: r.fascicolo_id,
          cognome: r.ospite_cognome,
          nome: r.ospite_nome,
          count: 0,
          stato: r.stato,
          latestId: r.id,
        });
      }
      const entry = map.get(key);
      entry.count += 1;
      entry.latestId = r.id;
      entry.stato = r.stato;
    });
    return Array.from(map.values());
  }, [items]);

  const filteredOspiti = ospitiConRicette.filter((o) => {
    const hay = `${o.cognome || ""} ${o.nome || ""}`.toLowerCase();
    return !ospiteSearch || hay.includes(ospiteSearch.toLowerCase());
  });

  const filteredItems = items.filter((r) => {
    const haystack = `${r.medico || ""} ${r.ospite_cognome || ""} ${r.ospite_nome || ""}`.toLowerCase();
    const matchSearch = !search || haystack.includes(search.toLowerCase());
    const matchOspite = !selectedOspite || r.fascicolo_id === selectedOspite.fascicolo_id
      || (`${r.ospite_cognome}-${r.ospite_nome}` === `${selectedOspite.cognome}-${selectedOspite.nome}`);
    const matchStato = !filterStato || r.stato === filterStato;
    let matchPeriodo = true;
    if (filterPeriodo && filterPeriodo !== "tutti") {
      const giorni = parseInt(filterPeriodo, 10);
      const cutoff = new Date(Date.now() - giorni * 86400000);
      const dt = r.data_ricezione || r.created_at;
      if (dt) {
        matchPeriodo = new Date(dt) >= cutoff;
      }
    }
    return matchSearch && matchOspite && matchStato && matchPeriodo;
  });

  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((r) => checkedIds.has(r.id));
  const someFilteredSelected = filteredItems.some((r) => checkedIds.has(r.id));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filteredItems.map((r) => r.id)));
    }
  };

  const handleExportCSV = () => {
    if (filteredItems.length === 0) { toast.info("Nessun dato da esportare"); return; }
    const cols = ["Data ricezione", "Medico", "Ospite", "Stato", "Scadenza", "Allegati", "Farmaci"];
    const rows = filteredItems.map(r => [
      r.data_ricezione ? new Date(r.data_ricezione).toLocaleDateString("it-IT") : "",
      r.medico || "",
      `${r.ospite_cognome || ""} ${r.ospite_nome || ""}`.trim(),
      r.stato || "",
      r.scadenza || "",
      (r.allegati || []).length,
      (r.farmaci_rilevati || []).map(f => f.farmaco).join("; "),
    ]);
    const csv = [cols, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ricette_medico_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Clock} label="Ricette attese dal medico" value={kpi.ricette_attese} subtitle={`${kpi.ricette_attese || 0} in scadenza`} tone="violet" />
        <KpiCard icon={Clock} label="Ricette parziali da completare" value={kpi.ricette_parziali} subtitle="da completare" tone="amber" />
        <KpiCard icon={CheckCircle} label="Ricette ricevute da verificare" value={kpi.ricette_ricevute} subtitle="da verificare" tone="blue" />
        <KpiCard icon={Send} label="Inviate alla farmacia" value={kpi.inviate_farmacia} subtitle="In attesa risposta" tone="emerald" />
        <KpiCard icon={FileText} label="Totali ricevute" value={kpi.totale_ricevute} subtitle="Ultimi 30 giorni" tone="slate" />
      </div>

      <div className="grid xl:grid-cols-12 gap-4">
        {/* Colonna ospiti */}
        <div className="xl:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-3 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Seleziona ospite</h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                className="w-full rounded-lg border border-slate-200 pl-9 pr-2.5 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                placeholder="Cerca ospite..."
                value={ospiteSearch}
                onChange={(e) => setOspiteSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-[480px] overflow-y-auto p-2 space-y-1">
            <button
              type="button"
              onClick={() => setSelectedOspite(null)}
              className={`w-full text-left rounded-lg px-2.5 py-2.5 text-sm transition ${!selectedOspite ? "bg-violet-50 text-violet-800 font-semibold" : "text-slate-700 hover:bg-slate-50 font-medium"}`}
            >
              Tutti gli ospiti
            </button>
            {filteredOspiti.map((o) => (
              <button
                key={o.fascicolo_id || `${o.cognome}-${o.nome}`}
                type="button"
                onClick={() => { setSelectedOspite(o); openDetail(o.latestId); }}
                className={`w-full text-left rounded-lg px-2.5 py-2.5 transition flex items-start gap-2.5 ${selectedOspite?.fascicolo_id === o.fascicolo_id ? "bg-violet-50 border border-violet-200" : "hover:bg-slate-50 border border-transparent"}`}
              >
                <OspiteAvatar cognome={o.cognome} nome={o.nome} className="w-8 h-8 text-xs mt-0.5" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-sm font-semibold text-slate-900 leading-snug break-words">
                    {o.cognome} {o.nome}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500">{o.count} ricette</span>
                    <StatoBadge stato={o.stato} map={STATO_RICETTA_LABELS} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tabella centrale */}
        <div className={`${selected ? "xl:col-span-6" : "xl:col-span-9"}`}>
          <div className="mb-3">
            <FilterBar
              filters={(
                <>
                  <FilterSelect value={filterPeriodo} onChange={e => setFilterPeriodo(e.target.value)}>
                    <option value="7">Ultimi 7 giorni</option>
                    <option value="30">Ultimi 30 giorni</option>
                    <option value="90">Ultimi 90 giorni</option>
                    <option value="tutti">Tutti</option>
                  </FilterSelect>
                  <FilterSelect value={filterStato} onChange={e => setFilterStato(e.target.value)}>
                    <option value="">Tutti gli stati</option>
                    <option value="in_attesa">In attesa medico</option>
                    <option value="da_completare">Da completare</option>
                    <option value="da_verificare">Da verificare</option>
                    <option value="da_inviare_farmacia">Da inviare farmacia</option>
                    <option value="inviata_farmacia">Inviata farmacia</option>
                    <option value="completata">Completata</option>
                  </FilterSelect>
                </>
              )}
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Cerca ricetta..."
              action={
                <div className="flex gap-2">
                  <Btn variant="secondary" onClick={handleExportCSV} title="Esporta CSV"><Download className="w-4 h-4" /></Btn>
                  <Btn onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Carica ricetta</Btn>
                </div>
              }
            />
          </div>

          {checkedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 mb-3">
              <span className="text-sm font-medium text-violet-900">
                {checkedIds.size} ricett{checkedIds.size === 1 ? "a selezionata" : "e selezionate"}
              </span>
              <Btn variant="secondary" className="text-sm" onClick={() => handleInviaFarmacia()} disabled={actionLoading}>
                <Send className="w-4 h-4" /> Invia alla farmacia
              </Btn>
              <Btn variant="success" className="text-sm" onClick={() => handleCompleta()} disabled={actionLoading}>
                <CheckCircle className="w-4 h-4" /> Segna come completata
              </Btn>
              <Btn variant="danger" className="text-sm" onClick={handleDeleteSelected} disabled={actionLoading}>
                <Trash2 className="w-4 h-4" /> Elimina
              </Btn>
              <button type="button" className="text-sm text-violet-700 hover:underline" onClick={() => setCheckedIds(new Set())}>
                Deseleziona tutte
              </button>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Ricette del medico</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="p-3 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                        checked={allFilteredSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                        }}
                        onChange={toggleSelectAllFiltered}
                        title="Seleziona tutte le ricette filtrate"
                        aria-label="Seleziona tutte"
                      />
                    </th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Data ricezione</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Medico</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Ospite</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs hidden md:table-cell">Allegati</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Stato</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs hidden lg:table-cell" title="Inviata · Consegnata · Aperta">Email</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Scadenza</th>
                    <th className="p-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-500">Nessuna ricetta</td></tr>
                  ) : filteredItems.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b border-slate-50 hover:bg-violet-50/40 cursor-pointer transition ${selected?.id === r.id ? "bg-violet-50/60" : ""} ${checkedIds.has(r.id) ? "bg-violet-50/30" : ""}`}
                      onClick={() => openDetail(r.id)}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                          checked={checkedIds.has(r.id)}
                          onChange={() => toggleChecked(r.id)}
                          aria-label={`Seleziona ricetta ${r.ospite_cognome} ${r.ospite_nome}`}
                        />
                      </td>
                      <td className="p-3 text-slate-600 whitespace-nowrap">{formatDate(r.data_ricezione)}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800 truncate">{r.medico || "—"}</div>
                        <div className="text-xs text-slate-400">Medico di base</div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <OspiteAvatar cognome={r.ospite_cognome} nome={r.ospite_nome} className="w-7 h-7 text-[10px]" />
                          <span className="truncate">{r.ospite_cognome} {r.ospite_nome}</span>
                        </div>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <FileText className="w-3.5 h-3.5" />
                          {(r.allegati || []).length}
                        </div>
                      </td>
                      <td className="p-3"><StatoBadge stato={r.stato} map={STATO_RICETTA_LABELS} /></td>
                      <td className="p-3 hidden lg:table-cell">
                        <EmailTrackingBadges item={r} compact />
                      </td>
                      <td className="p-3"><ScadenzaCell date={r.scadenza} /></td>
                      <td className="p-3">
                        <button type="button" className="p-1.5 text-slate-400 hover:text-violet-600 rounded-lg hover:bg-violet-50" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pannello dettaglio */}
        {selected && (
          <DetailPanel
            title="Dettaglio ricetta"
            icon={FileText}
            onClose={() => setSelected(null)}
            className="xl:col-span-3 max-h-[580px]"
          >
            <div className="rounded-lg bg-violet-50/60 border border-violet-100 p-3">
              <div className="flex items-center gap-3">
                <OspiteAvatar cognome={selected.ospite_cognome} nome={selected.ospite_nome} />
                <div>
                  <div className="font-semibold text-slate-900">{selected.ospite_cognome} {selected.ospite_nome}</div>
                  <div className="text-xs text-slate-500">{selected.medico || "Medico non indicato"}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <StatoBadge stato={selected.stato} map={STATO_RICETTA_LABELS} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-400 block">Data ricezione</span><span className="font-medium">{formatDate(selected.data_ricezione)}</span></div>
              <div><span className="text-slate-400 block">Scadenza</span><ScadenzaCell date={selected.scadenza} /></div>
            </div>

            <EmailTrackingBadges item={selected} />

            {(selected.allegati || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Allegati</div>
                <ul className="space-y-1">
                  {(selected.allegati || []).map((a, i) => (
                    <li key={a.id || i} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-100">
                      <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      <span className="flex-1 truncate">{a.file_name || "Allegato"}</span>
                      <button
                        type="button"
                        className="text-slate-500 hover:text-violet-700 p-0.5"
                        title="Visualizza"
                        onClick={() => handleView(a.id)}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="text-violet-600 hover:text-violet-800 p-0.5"
                        title="Scarica"
                        onClick={() => handleDownload(a.id, a.file_name)}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Confronto primario: farmaci ricetta vs richieste farmacia del mese */}
            {selected.analisi_ia?.confronto_farmacia && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700">
                    Confronto con richieste farmacia
                    {selected.analisi_ia.confronto_farmacia.mese && (
                      <span className="ml-1 font-normal text-slate-400">({selected.analisi_ia.confronto_farmacia.mese})</span>
                    )}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-400">
                    {selected.analisi_ia.confronto_farmacia.n_richieste_mese ?? 0} richieste nel mese
                  </span>
                </div>
                <div className="p-2.5 space-y-1.5">
                  {(selected.analisi_ia.confronto_farmacia.farmaci_mancanti || []).length === 0 &&
                   (selected.analisi_ia.confronto_farmacia.farmaci_coperti || []).length === 0 && (
                    <p className="text-xs text-slate-400">Nessun farmaco da confrontare. Analizza prima i documenti.</p>
                  )}
                  {(selected.analisi_ia.confronto_farmacia.farmaci_mancanti || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                      <span className="text-xs font-medium text-amber-900 flex-1">{f}</span>
                      <span className="text-[10px] text-amber-600">non in richiesta</span>
                    </div>
                  ))}
                  {(selected.analisi_ia.confronto_farmacia.farmaci_coperti || []).map((f, i) => {
                    const dettaglio = (selected.analisi_ia.confronto_farmacia.farmaci_coperti_dettaglio || [])
                      .find((d) => d.farmaco === f);
                    const isEquivalente = dettaglio && dettaglio.tipo_match && dettaglio.tipo_match !== "nome";
                    return (
                      <div key={i} className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                        <span className="text-xs text-emerald-900 flex-1">
                          {f}
                          {isEquivalente && (
                            <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600/80">
                              equivalente di {dettaglio.corrispondenza}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-emerald-600">già richiesto</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Anomalie terapia (secondario, collassabile) */}
            {(selected.analisi_ia?.anomalie || []).length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 text-xs font-medium text-slate-600 transition"
                  onClick={() => setShowAnomalie((v) => !v)}
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  Verifica terapia ({selected.analisi_ia.anomalie.length})
                  <span className="ml-auto">{showAnomalie ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</span>
                </button>
                {showAnomalie && (
                  <ul className="px-3 py-2 space-y-0.5 text-xs text-slate-600 bg-white">
                    {selected.analisi_ia.anomalie.map((a, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                        {a.messaggio || a.tipo}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Farmaci prescritti ({(selected.farmaci_rilevati || []).length})</div>
              {(selected.farmaci_rilevati || []).length === 0 ? (
                <p className="text-xs text-slate-400">Nessun farmaco estratto. Carica un allegato e usa Analizza.</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selected.farmaci_rilevati.map((f) => (
                    <div key={f.id} className="border border-slate-100 rounded-lg p-2.5 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                        <div className="font-medium text-slate-900 text-xs">{f.farmaco}</div>
                      </div>
                      <div className="mt-0.5 text-xs text-slate-600 pl-3.5">{f.dosaggio} — {f.frequenza}</div>
                      <select
                        className="w-full mt-1.5 rounded-lg border border-slate-200 px-2 py-1 text-xs bg-white"
                        value={f.destinazione_ricetta || "da_verificare"}
                        onChange={(e) => handleDestinazione(f.id, e.target.value)}
                      >
                        {DESTINAZIONE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-violet-50 border border-violet-100 p-3">
              <div className="text-xs font-semibold text-violet-800 mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Analisi documenti
              </div>
              <Btn variant="soft" className="w-full text-xs" onClick={handleAnalizzaIA} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Analizza
              </Btn>
            </div>

            <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
              <Btn variant="secondary" className="w-full text-xs" onClick={() => fileRef.current?.click()} disabled={actionLoading}>
                <Upload className="w-3.5 h-3.5" /> Carica allegato
              </Btn>
              <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleUpload} />
              <Btn className="w-full text-xs" onClick={() => handleInviaFarmacia()} disabled={actionLoading}><Send className="w-3.5 h-3.5" /> Invia alla farmacia</Btn>
              <div className="grid grid-cols-2 gap-2">
                <Btn variant="secondary" className="text-xs" onClick={handleStampa}><Printer className="w-3.5 h-3.5" /> Stampa</Btn>
                <Btn variant="success" className="text-xs" onClick={() => handleCompleta([selected.id])} disabled={actionLoading}><CheckCircle className="w-3.5 h-3.5" /> Completa</Btn>
              </div>
              <Btn variant="secondary" className="w-full text-xs" onClick={handleSpostaARichiesta} disabled={actionLoading}>
                <ArrowLeftRight className="w-3.5 h-3.5" /> Sposta → Richieste farmacia
              </Btn>
              <Btn variant="danger" className="w-full text-xs" onClick={handleDelete} disabled={actionLoading}>
                <Trash2 className="w-3.5 h-3.5" /> Elimina ricetta
              </Btn>
            </div>
          </DetailPanel>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-xl">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Nuova ricetta medico</h3>
              <button type="button" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Ospite</label>
                <select className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.fascicolo_id} onChange={(e) => handleOspiteChange(e.target.value)}>
                  <option value="">Seleziona...</option>
                  {ospiti.map((o) => (
                    <option key={o.fascicolo_id} value={o.fascicolo_id}>{o.cognome} {o.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Medico</label>
                <DoctorSelect
                  token={token}
                  value={form.doctor_id}
                  displayName={form.medico}
                  onSelect={(doctor) => setForm({ ...form, doctor_id: doctor.id, medico: doctor.name })}
                  onClear={() => setForm({ ...form, doctor_id: "", medico: "" })}
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Btn variant="secondary" onClick={() => setShowCreate(false)}>Annulla</Btn>
                <Btn onClick={handleCreate} disabled={actionLoading}>Crea</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
